/**
 * MORVIX AI — Creative Consultant knowledge base.
 *
 * This file is the single source of truth for everything the consultant knows.
 * It is intentionally data-only so it can later be shipped to a secure backend
 * (vector store / system prompt) without touching a single component.
 */

import { brand, contact, links, process as processSteps, services, whatsappUrl } from './site'

export type ConsultantAction = {
  label: string
  /** send = feed back into the consultant · link = external · route = in-app · brief = start qualification · booking = open the scheduler */
  kind: 'send' | 'link' | 'route' | 'brief' | 'booking'
  value: string
}

export type KnowledgeTopic = {
  id: string
  keywords: string[]
  answer: string
  actions?: ConsultantAction[]
}

export type ServiceGuide = {
  id: string
  name: string
  /** One-line positioning used in recommendations. */
  tagline: string
  /** Conversational explanation of the service. */
  what: string
  deliverables: string[]
  timeline: string
  /** Indicative guidance only — always confirmed on a consultation. */
  priceGuide: string
  aliases: string[]
}

/* ------------------------------------------------------------------ */
/* Identity                                                            */
/* ------------------------------------------------------------------ */

export const consultantIdentity = {
  name: 'Morvix AI',
  role: 'Creative Consultant',
  status: 'Online · replies instantly',
}

export const consultantIntro = `Hi! I'm Morvix AI, your Creative Consultant.

I'm here to help you choose the right design service, answer questions about our process, estimate your project, showcase our work, and guide you through starting your project with Morvix Graphics.

How can I help you today?`

/* ------------------------------------------------------------------ */
/* Shared call-to-action set                                           */
/* ------------------------------------------------------------------ */

export const CTA = {
  portfolio: { label: 'View Portfolio', kind: 'link', value: links.portfolio } as ConsultantAction,
  reviews: { label: 'Client Reviews', kind: 'link', value: links.reviews } as ConsultantAction,
  whatsapp: { label: 'Chat on WhatsApp', kind: 'link', value: whatsappUrl } as ConsultantAction,
  contact: { label: 'Contact Page', kind: 'route', value: '/contact' } as ConsultantAction,
  work: { label: 'See Case Studies', kind: 'route', value: '/work' } as ConsultantAction,
  servicesPage: { label: 'All Services', kind: 'route', value: '/services' } as ConsultantAction,
  brief: { label: 'Start a Project', kind: 'brief', value: 'brief' } as ConsultantAction,
  consult: { label: 'Book a Consultation', kind: 'booking', value: 'consultation' } as ConsultantAction,
  quote: { label: 'Get a Quote', kind: 'brief', value: 'brief' } as ConsultantAction,
  contactUs: { label: 'Contact Us', kind: 'route', value: '/contact' } as ConsultantAction,
}

/* ------------------------------------------------------------------ */
/* Business policy — pricing, delivery & revisions                     */
/* ------------------------------------------------------------------ */

/**
 * The consultant never quotes prices, promises delivery dates or commits to a
 * revision policy. Those decisions belong to the Morvix Graphics team, so the
 * consultant answers professionally and hands the visitor over instead.
 */
export const policyReplies = {
  pricing: {
    text: `Every project is unique, so we don't provide pricing through the chatbot. To receive an accurate quotation tailored to your project, please request a custom quote or complete our contact form. Our team will review your requirements and get back to you.`,
    actions: [CTA.quote, CTA.contactUs, CTA.consult] as ConsultantAction[],
  },
  delivery: {
    text: `Project timelines vary depending on the scope and complexity of each project. After reviewing your requirements, our team will provide an estimated delivery schedule during the consultation process.`,
    actions: [CTA.quote, CTA.contactUs, CTA.consult] as ConsultantAction[],
  },
  revisions: {
    text: `Our team will discuss the project workflow and revision process after reviewing your project requirements, ensuring we recommend the most suitable approach for your needs.`,
    actions: [CTA.quote, CTA.contactUs] as ConsultantAction[],
  },
  handoff: {
    text: `I'd be happy to help you take the next step. To ensure you receive accurate information tailored to your project, please request a custom quote or complete our contact form. A member of the Morvix Graphics team will review your requirements and get back to you.`,
    actions: [CTA.quote, CTA.consult, CTA.contactUs, CTA.whatsapp] as ConsultantAction[],
  },
}

/* ------------------------------------------------------------------ */
/* Welcome quick actions                                               */
/* ------------------------------------------------------------------ */

export const quickActions: string[] = [
  'Explore Our Services',
  'View Portfolio',
  'Brand Identity',
  'Logo Design',
  'Website Design',
  'Motion Graphics',
  'Packaging Design',
  'Social Media Design',
  'Pricing & Consultation',
  'Contact Us',
]

/* ------------------------------------------------------------------ */
/* Service guides                                                      */
/* ------------------------------------------------------------------ */

export const serviceGuide: ServiceGuide[] = [
  {
    id: 'brand-identity',
    name: 'Brand Identity',
    tagline: 'A complete visual system, not just a mark',
    what: "Brand Identity is our flagship work. We start with positioning and personality, then build the full visual system around it — logo suite, type hierarchy, colour, layout rules and the guidelines that keep everything consistent as you grow.",
    deliverables: [
      'Primary logo + responsive variations',
      'Colour palette & typography system',
      'Brand guidelines document',
      'Core applications & mockups',
      'Master files in every format',
    ],
    timeline: '2–4 weeks depending on scope and feedback speed',
    priceGuide: 'Full identity systems typically start around $650 and scale with depth of system and number of applications',
    aliases: ['brand identity', 'branding', 'brand', 'identity', 'rebrand', 'brand system', 'brand guidelines', 'visual identity'],
  },
  {
    id: 'logo-design',
    name: 'Logo Design',
    tagline: 'One distinctive mark, engineered to scale',
    what: "Logo Design is focused work: we design a distinctive mark built around your positioning, test it small and large, and hand over a clean, scalable file set. Most clients later grow it into a full identity system.",
    deliverables: [
      'Primary logo + secondary lockups',
      'Monogram / icon mark',
      'Black, white & colour versions',
      'Vector + raster export pack',
      'Basic usage sheet',
    ],
    timeline: '5–10 days',
    priceGuide: 'Logo-only projects usually land in the $150–$450 range depending on concept rounds and application needs',
    aliases: ['logo', 'logo design', 'logomark', 'monogram', 'wordmark', 'icon'],
  },
  {
    id: 'packaging-design',
    name: 'Packaging Design',
    tagline: 'Shelf presence that communicates value',
    what: "Packaging Design is where brands earn or lose the sale in three seconds. We design structure-aware artwork — hierarchy, materials, finish cues — and deliver print-ready files your printer can run without questions.",
    deliverables: [
      'Packaging artwork & dielines',
      'Label / carton / pouch layouts',
      'Print-ready production files',
      '3D-style product mockups',
    ],
    timeline: '1–3 weeks depending on SKU count',
    priceGuide: 'Single-SKU packaging generally starts around $200, with range pricing for multi-SKU families',
    aliases: ['packaging', 'package', 'label', 'product label', 'box', 'carton', 'pouch'],
  },
  {
    id: 'social-media-design',
    name: 'Social Media Design',
    tagline: 'A feed that looks designed, not decorated',
    what: "Social Media Design gives you a repeatable visual system — templates, grid logic and content styles — so every post looks unmistakably yours whether we design it or your team does.",
    deliverables: [
      'Post & story template system',
      'Grid / feed art direction',
      'Editable template files',
      'Monthly content packs (optional)',
    ],
    timeline: '4–10 days for a starter system',
    priceGuide: 'Template systems typically start around $120; ongoing monthly content packs are quoted per volume',
    aliases: ['social media', 'social', 'instagram', 'feed', 'posts', 'content design', 'flyer', 'flyers'],
  },
  {
    id: 'website-design',
    name: 'Website Design',
    tagline: 'A site that converts, not just looks good',
    what: "Website Design covers the full experience: structure, messaging hierarchy, responsive layouts and a design system your developer can build cleanly. We design for the decision the visitor needs to make.",
    deliverables: [
      'Sitemap & content structure',
      'Desktop + mobile page designs',
      'Reusable UI component set',
      'Developer-ready handoff',
    ],
    timeline: '2–5 weeks depending on page count',
    priceGuide: 'Landing pages typically start around $350; multi-page sites are quoted after a short scoping call',
    aliases: ['website', 'web design', 'web', 'landing page', 'site', 'webpage'],
  },
  {
    id: 'motion-graphics',
    name: 'Motion Graphics',
    tagline: 'Brands that move with intent',
    what: "Motion Graphics brings the identity to life — logo animations, promo edits, explainers and social-native motion. Everything is built on the same design language as your static work so nothing feels borrowed.",
    deliverables: [
      'Logo sting / animated mark',
      'Promo or explainer video',
      'Social-cut versions (1:1, 9:16, 16:9)',
      'Source project files',
    ],
    timeline: '1–3 weeks depending on duration and complexity',
    priceGuide: 'Logo animations typically start around $150; longer promos and explainers are quoted per second of finished motion',
    aliases: ['motion', 'motion graphics', 'animation', 'animated', 'video', 'reel', 'explainer'],
  },
  {
    id: 'ui-ux-design',
    name: 'UI/UX Design',
    tagline: 'Interfaces built around real user flows',
    what: "UI/UX Design starts with the flow, not the screen. We map what users are trying to do, then design clear, accessible interfaces and a component library that keeps your product consistent as it ships.",
    deliverables: [
      'User flows & wireframes',
      'High-fidelity UI screens',
      'Design system / component library',
      'Interactive prototype',
    ],
    timeline: '2–6 weeks depending on product surface',
    priceGuide: 'Product UI work is scoped per flow — most engagements begin around $400 for a focused feature set',
    aliases: ['ui', 'ux', 'ui/ux', 'ui ux', 'app design', 'product design', 'dashboard', 'interface', 'presentation design'],
  },
  {
    id: 'print-design',
    name: 'Print Design',
    tagline: 'Print that holds up in the hand',
    what: "Print Design covers everything physical — posters, brochures, book covers, stationery, merchandise. We design with real production constraints in mind and deliver files ready for press.",
    deliverables: [
      'Print layouts & artwork',
      'Book / report covers',
      'Stationery & collateral',
      'Press-ready PDFs with bleed',
    ],
    timeline: '4–12 days depending on page count',
    priceGuide: 'Single-piece print work commonly starts around $80; multi-page publications are quoted per spread',
    aliases: ['print', 'poster', 'brochure', 'book cover', 'stationery', 'business card', 'merch', 'apparel', 'clothing design', 't-shirt'],
  },
  {
    id: 'creative-strategy',
    name: 'Creative Strategy',
    tagline: 'The thinking that makes the design work',
    what: "Creative Strategy is the layer most studios skip. Before anything visual, we clarify positioning, audience, competitive whitespace and message hierarchy — so every design decision afterwards has a reason behind it.",
    deliverables: [
      'Positioning & audience definition',
      'Competitive landscape review',
      'Messaging hierarchy',
      'Creative direction & moodboards',
    ],
    timeline: '1–2 weeks, usually folded into identity work',
    priceGuide: 'Included in full Brand Identity engagements, or available standalone from around $200',
    aliases: ['strategy', 'creative strategy', 'positioning', 'brand strategy', 'consulting', 'direction'],
  },
]

export const serviceById = (id: string) => serviceGuide.find((s) => s.id === id)

/* ------------------------------------------------------------------ */
/* General knowledge topics                                            */
/* ------------------------------------------------------------------ */

export const knowledge: KnowledgeTopic[] = [
  {
    id: 'about',
    keywords: ['who are you', 'about morvix', 'about you', 'what is morvix', 'tell me about', 'company', 'agency', 'founder', 'moses', 'experience', 'how long'],
    answer: `${brand.name} is a creative agency founded in ${brand.founded} by ${brand.founder} — ${brand.yearsLabel} years of practice, ${brand.serviceModel.toLowerCase()} We build brand identities, logos, packaging, digital experiences and motion for ambitious brands. Our whole philosophy sits in one line: ${brand.tagline}`,
    actions: [{ label: 'About Morvix', kind: 'route', value: '/about' }, CTA.work],
  },
  {
    id: 'services-overview',
    keywords: ['services', 'what do you do', 'what do you offer', 'offerings', 'explore our services', 'capabilities', 'help with'],
    answer: `We work across ${services.length} disciplines, but most projects start in one of these:\n\n• Brand Identity — full visual systems\n• Logo Design — a single distinctive mark\n• Packaging Design — shelf presence\n• Social Media Design — template systems\n• Website Design — structure to handoff\n• Motion Graphics — animated brand work\n• UI/UX Design — product interfaces\n• Print Design — physical collateral\n• Creative Strategy — the thinking underneath\n\nTell me which one is closest to what you need and I'll walk you through it — or describe your situation and I'll point you at the right one.`,
    actions: [CTA.servicesPage, { label: 'Help me choose', kind: 'send', value: 'help me choose a service' }],
  },
  {
    id: 'process',
    keywords: ['process', 'how do you work', 'how does it work', 'steps', 'workflow', 'what happens', 'stages'],
    answer: `Every project runs through the same six stages, so you always know where things stand:\n\n${processSteps
      .map((step) => `${step.step} · ${step.title} — ${step.description}`)
      .join('\n')}\n\nYou see work and give feedback at every stage — nothing gets built behind a curtain.`,
    actions: [CTA.consult, CTA.work],
  },
  {
    id: 'timeline',
    keywords: ['timeline', 'how long', 'how fast', 'turnaround', 'deadline', 'delivery time', 'duration', 'rush', 'urgent', 'when will'],
    answer: policyReplies.delivery.text,
    actions: policyReplies.delivery.actions,
  },
  {
    id: 'revisions',
    keywords: ['revision', 'revisions', 'changes', 'feedback', 'amend', 'edits', 'rounds', 'not happy'],
    answer: policyReplies.revisions.text,
    actions: policyReplies.revisions.actions,
  },
  {
    id: 'portfolio',
    keywords: ['portfolio', 'work', 'examples', 'case study', 'case studies', 'previous work', 'past work', 'view portfolio', 'samples', 'show me'],
    answer: `Happy to show you. Our case studies live on the Work page — including the Apparel Identity System and the Miceen Apparel Identity, both good examples of a single mark scaling across apparel, footwear and accessories. The wider archive (client confidentiality keeps a lot of it private) sits in our portfolio drive.`,
    actions: [CTA.work, CTA.portfolio, CTA.reviews],
  },
  {
    id: 'reviews',
    keywords: ['review', 'reviews', 'testimonial', 'testimonials', 'trustpilot', 'reputation', 'trust', 'legit'],
    answer: `Fair question to ask. We've worked with 30+ brands on 400+ projects, and verified client reviews are public on Trustpilot — worth a read before you commit to anything.`,
    actions: [CTA.reviews, CTA.work],
  },
  {
    id: 'pricing-general',
    keywords: ['pricing', 'price', 'prices', 'cost', 'how much', 'budget', 'rate', 'rates', 'quote', 'fee', 'affordable', 'expensive', 'pricing & consultation'],
    answer: policyReplies.pricing.text,
    actions: policyReplies.pricing.actions,
  },
  {
    id: 'consultation',
    keywords: ['consultation', 'consult', 'book', 'booking', 'call', 'meeting', 'talk to someone', 'speak to', 'discovery call', 'human'],
    answer: `A consultation is a short, no-pressure conversation about your brand, your goals and what you actually need — you'll leave with a clear recommendation whether or not you work with us. I can collect a few details now so the team comes to that conversation already prepared, or you can jump straight onto WhatsApp.`,
    actions: [CTA.consult, CTA.whatsapp, CTA.contact],
  },
  {
    id: 'contact',
    keywords: ['contact', 'contact us', 'email', 'whatsapp', 'phone', 'reach', 'get in touch', 'hours', 'available', 'support'],
    answer: `Here's the fastest way to reach the studio:\n\n• Email — ${contact.email}\n• WhatsApp — ${contact.whatsappDisplay}\n• ${brand.hours}\n\n${brand.serviceModel} Whichever channel you use, you'll get a real reply from the team.`,
    actions: [CTA.whatsapp, CTA.contact],
  },
  {
    id: 'files',
    keywords: ['file', 'files', 'formats', 'source', 'vector', 'ai file', 'psd', 'ownership', 'rights', 'copyright'],
    answer: `You get everything. On completion we hand over master vector files, print-ready and web-ready exports, plus the source project files — and full ownership of the final approved work transfers to you. No hostage files, no licensing surprises.`,
    actions: [CTA.consult],
  },
  {
    id: 'payment',
    keywords: ['payment', 'pay', 'deposit', 'installment', 'invoice', 'refund', 'terms', 'contract'],
    answer: policyReplies.handoff.text,
    actions: policyReplies.handoff.actions,
  },
  {
    id: 'location',
    keywords: ['where are you', 'location', 'based', 'country', 'remote', 'timezone', 'worldwide'],
    answer: `${brand.serviceModel} We work asynchronously with clients across time zones and keep ${brand.hours.toLowerCase()}, so being in a different country has never slowed a project down.`,
    actions: [CTA.whatsapp],
  },
  {
    id: 'getting-started',
    keywords: ['how do i start', 'how do we start', 'how to start', 'how do i begin', 'where do i start', 'first step', 'next step', 'next steps', 'onboarding', 'get going', 'how do i work with you'],
    answer: `Starting is deliberately simple:\n\n1 · Tell me what you're building — I'll ask a few questions and point you at the right service.\n2 · Share a short brief here, or send it through the contact form or WhatsApp.\n3 · Book a consultation — a short call where we discuss goals, scope and direction.\n4 · The team reviews everything and comes back to you with a tailored proposal.\n\nAfter that we move into Discover → Strategize → Create → Refine → Deliver → Launch. Want me to take your brief now, or would you rather book the call first?`,
    actions: [CTA.brief, CTA.consult, CTA.contactUs, CTA.whatsapp],
  },
  {
    id: 'international',
    keywords: ['international', 'internationally', 'worldwide', 'overseas', 'abroad', 'other countries', 'outside nigeria', 'global', 'anywhere in the world', 'different time zone', 'time zones', 'do you work with'],
    answer: `Yes — ${brand.serviceModel} We work with clients across time zones entirely remotely: everything happens over email, WhatsApp and scheduled calls, files are delivered digitally, and ${brand.hours.toLowerCase()} keeps feedback moving. Where you are has never slowed a project down.`,
    actions: [CTA.consult, CTA.whatsapp, CTA.work],
  },
  {
    id: 'site-guide',
    keywords: ['website pages', 'what pages', 'where can i find', 'navigate the site', 'site map', 'show me the site', 'more information'],
    answer: `Everything lives in five places:\n\n• Home — who we are and the highlights\n• Services — all ${services.length} disciplines in detail\n• Work — case studies and the portfolio\n• About — the studio, values and process\n• Contact — the form, WhatsApp and consultation booking\n\nTell me what you're after and I'll take you straight there.`,
    actions: [CTA.servicesPage, CTA.work, CTA.contactUs],
  },
  {
    id: 'industries',
    keywords: ['industry', 'industries', 'niche', 'sector', 'do you work with startups', 'small business', 'clients you work with', 'type of clients'],
    answer: `We work across fashion and apparel, food and drink, tech and SaaS, beauty and wellness, real estate and lifestyle — from first-time founders to established brands repositioning themselves. The industry changes the visual direction, not the standard of the work. Which sector are you in?`,
    actions: [CTA.work, { label: 'Help me choose', kind: 'send', value: 'help me choose a service' }],
  },
  {
    id: 'why-morvix',
    keywords: ['why morvix', 'why you', 'different', 'better', 'compare', 'competitor', 'why should'],
    answer: `Three honest reasons: we start with strategy rather than decoration, we design systems that hold up across every touchpoint instead of one pretty file, and you deal directly with the people doing the work. ${brand.yearsLabel} years, 400+ projects, and a portfolio you can inspect before you spend anything.`,
    actions: [CTA.work, CTA.reviews, CTA.consult],
  },
]

/* ------------------------------------------------------------------ */
/* Consultative discovery flow                                         */
/* ------------------------------------------------------------------ */

export type DiscoveryStep = {
  key: 'stage' | 'industry' | 'scope' | 'budget'
  question: string
  options: string[]
}

export const discoveryQuestions: DiscoveryStep[] = [
  {
    key: 'stage',
    question: "Great — let's make sure we point you at the right thing. Is this for a brand new business, or an existing one?",
    options: ['Brand new business', 'Existing business', 'Rebrand / refresh'],
  },
  {
    key: 'industry',
    question: 'Noted. What industry is your business in?',
    options: ['Fashion & apparel', 'Food & drink', 'Tech / SaaS', 'Beauty & wellness', 'Real estate', 'Something else'],
  },
  {
    key: 'scope',
    question: 'And what kind of help are you looking for — a logo on its own, or a complete brand identity system?',
    options: ['Logo only', 'Complete identity system', 'Not sure — advise me'],
  },
  {
    key: 'budget',
    question: "Last one, and it genuinely helps me recommend the right thing rather than the biggest thing: what budget range are you working with?",
    options: ['Under $300', '$300 – $800', '$800 – $2,000', '$2,000+', 'Still deciding'],
  },
]

/* ------------------------------------------------------------------ */
/* Project qualification                                               */
/* ------------------------------------------------------------------ */

export type BriefField = {
  key: 'name' | 'email' | 'company' | 'industry' | 'service' | 'budget' | 'timeline' | 'goals' | 'notes'
  label: string
  question: string
  options?: string[]
  optional?: boolean
  validate?: 'email'
}

export const briefFields: BriefField[] = [
  { key: 'name', label: 'Full name', question: "Let's get this on the studio's desk. First — what's your full name?" },
  { key: 'email', label: 'Email', question: 'Thanks. What email should the team reply to?', validate: 'email' },
  { key: 'company', label: 'Company / brand', question: "And what's the company or brand name? (type “none yet” if it isn't named yet)" },
  {
    key: 'industry',
    label: 'Industry',
    question: 'What industry are you in?',
    options: ['Fashion & apparel', 'Food & drink', 'Tech / SaaS', 'Beauty & wellness', 'Real estate', 'Something else'],
  },
  {
    key: 'service',
    label: 'Service needed',
    question: 'Which service do you need?',
    options: ['Brand Identity', 'Logo Design', 'Packaging Design', 'Website Design', 'Motion Graphics', 'Social Media Design'],
  },
  {
    key: 'budget',
    label: 'Estimated budget',
    question: 'What estimated budget are you working with?',
    options: ['Under $300', '$300 – $800', '$800 – $2,000', '$2,000+', 'Still deciding'],
  },
  {
    key: 'timeline',
    label: 'Timeline',
    question: 'When do you need this delivered?',
    options: ['ASAP / rush', 'Within 2 weeks', 'This month', '1–3 months', 'Just planning ahead'],
  },
  { key: 'goals', label: 'Project goals', question: "In a sentence or two — what do you want this project to achieve?" },
  { key: 'notes', label: 'Additional notes', question: 'Anything else the team should know? References, competitors, must-haves — or type “skip”.', optional: true },
]
